import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { addUserEducation, checkNIP, deleteUserEducation, getEducation } from '../../utilities/sendRequest';
import DeleteIcon from '@mui/icons-material/Delete';

const CardEmployee = ({ slug, fetchAgain, setFetchAgain }) => {
    const [isReady, setIsReady] = useState(false)
    const [fetchMore, setFetchMore] = useState(false)
    const [education, setEducation] = useState([])
    const [nip, setNip] = useState("")
    const [fullname, setFullname] = useState("")
    const [employeeId, setEmployeeId] = useState("")
    const [datas, setDatas] = useState([])
    const [isChecking, setIsCheking] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [isValid, setIsValid] = useState(false)
    const [debouncedCode, setDebouncedCode] = useState("")

    useEffect(() => {
        if (education) {
            fetchParticipants();
            setFetchMore(false);
            setFetchAgain(false);
        }
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchMore, fetchAgain, education])

    useEffect(() => {
        const isReady = (nip !== "" && employeeId !== "" && isValid === true);
        setIsReady(isReady);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employeeId, isValid])

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedCode(nip);
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [nip]);

    useEffect(() => {
        if (!debouncedCode) {
            setIsValid(true)
            setIsCheking(false)
            return;
        }
        const handleCode = async () => {
            setIsLoading(true)
    
            checkNIP(debouncedCode).then((res) => {
                if (res.payload.is_valid) {
                    setIsValid(true)
                    setFullname(res.payload.fullname)
                    setEmployeeId(res.payload.id)
                } else {
                    setIsValid(false)
                    setFullname("")
                    setEmployeeId("")
                }
            }).catch((err) => {
                console.log(err)
                setIsValid(false)
                setFullname("")
                setEmployeeId("")
            }).finally(() => {
                setIsLoading(false)
                setIsCheking(true)
            })
        }
        handleCode()
    }, [debouncedCode]);

    const fetchParticipants = async () => {
        getEducation(slug).then((res) => {
            setDatas(
                res.payload.users.sort((p1, p2) => {
                    return new Date(p2.joinedAt) - new Date(p1.joinedAt)
                })
            )
            setEducation(res.payload)
        }).catch((err) => {
            toast.error(err, {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: 3000
            });
        })  
    }

    const columns = [
        {
            field: 'fullname',
            headerName: 'Employee',
            width: 250,
            renderCell: (params) => {
                return (
                    <div className="font-ssp">
                        {params.row.fullname}
                    </div>
                )
            } 
        },
        {
            field: 'nip',
            headerName: 'NIP',
            width: 100,
            renderCell: (params) => {
                return (
                    <div className="font-ssp">
                        {params.row.nip}
                    </div>
                )
            } 
        },
        {
            field: 'echelon',
            headerName: 'Eselon',
            width: 100,
            renderCell: (params) => {
                return (
                    <div className="font-ssp">
                        {params.row.echelon}
                    </div>
                )
            } 
        },
        {
            field: 'office',
            headerName: 'Office',
            width: 250,
            renderCell: (params) => {
                return (
                    <div className="font-ssp">
                        {params.row.office}
                    </div>
                )
            } 
        },
        {
            field: 'action',
            headerName: 'Action',
            width: 150,
            renderCell: (params) => {
                return (
                    <div className='w-full flex flex-row gap-3 items-center'>
                        {/* <VisibilityIcon onClick={() => handleDetail(params.row)} fontSize='medium' className="text-slate-400 hover:text-slate-500 cursor-pointer"/> */}
                        <DeleteIcon onClick={() => handleDelete(params.row.id)} className="text-red-400 hover:text-red-500 cursor-pointer"/>
                    </div>
                )
            }
        },
    ]

    const handleReset = () => {
        setEmployeeId("")
        setNip("")
        setFullname("")
    }

    const handleDelete = async (paramsId) => {
        deleteUserEducation(paramsId).then((res) => {
            toast.success(res.message, {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: 3000
            });
            setFetchMore(true)
        }).catch((error) => {
            toast.error(error.response.data.message, {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: 3000
            });
        })
    }

    const handleCreate = async (e) => {
        e.preventDefault()
    
        setIsReady(false);
    
        const reqData = {
            user_id: employeeId,
            is_graduate: false,
            certificate: null,
            education_id: education.id
        }
    
        addUserEducation(reqData).then((res) => {
            toast.success(res.message, {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: 3000
            });
            setFetchMore(true)
            handleReset();
        }).catch((err) => {
            toast.error(err.response.data.message, {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: 3000
            });
        }).finally(() => {
            setIsReady(true);
        })
    }

    return (
        <div className={`my-5 flex flex-col lg:flex-row justify-start gap-7 md:gap-10`}>
            {/* list data */}
            <div className='w-full h-full lg:w-[70%] flex flex-col gap-3 items-center'>
                <div className="p-2 w-full flex bg-white drop-shadow-lg rounded-md">
                    <DataGrid
                        autoHeight {...datas}
                        rows={datas}
                        columns={columns}
                        pageSize={5}
                        getRowId={(row) => row.id}
                        rowsPerPageOptions={[5]}
                        checkboxSelection
                        disableSelectionOnClick
                        components={{ Toolbar: GridToolbar, }}
                    />
                </div>
            </div>

            <div className="px-2 py-4 w-full h-fit lg:w-[30%] bg-white drop-shadow-lg rounded-md flex flex-col gap-3">
                <h1 className='font-semibold text-base font-ssp px-3'>Add Participant</h1>
                <hr className='px-3'/>
                <div className=" py-1 w-full h-fit flex">
                    <form onSubmit={handleCreate} className='px-5 w-full flex flex-col justify-between'>
                        {/* nip */}
                        <div className="mt-3 mb-6 w-full relative z-0 group">
                            <input 
                                minLength={5} 
                                value={nip} 
                                onChange={(e) => setNip(e.target.value)} 
                                type="text" 
                                name="nip" 
                                id="nip" 
                                className={`block py-2.5 px-2 w-full text-sm text-gray-900 bg-transparent 
                                            border-0 border-b-2 border-gray-300 appearance-none focus:outline-none focus:ring-0 
                                            ${isChecking ? isLoading ? 'peer-focus:border-yellow-600' : isValid ? 'peer-focus:border-green-600' : 'peer-focus:border-red-600' : 'peer-focus:border-gray-400'} peer`}
                                placeholder=" " 
                                required 
                            />
                            <label 
                                htmlFor="nip" 
                                className={`absolute top-3 -z-10 text-sm text-gray-500 
                                            duration-300 transform -translate-y-6 scale-75 origin-[0] peer-focus:left-0 peer-focus:font-medium 
                                            ${isChecking ? isLoading ? 'peer-focus:border-yellow-600' : isValid ? 'peer-focus:border-green-600' : 'peer-focus:border-red-600' : 'peer-focus:border-gray-400'} peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 
                                            peer-focus:scale-75 peer-focus:-translate-y-6`}
                            >NIP</label>
                        </div>
                        {/* fullname */}
                        <div className="mt-3 mb-6 w-full relative z-0 group">
                            <input 
                                value={fullname} 
                                onChange={(e) => setFullname(e.target.value)} 
                                type="text" 
                                name="fullname" 
                                id="fullname" 
                                className="block py-2.5 px-2 w-full text-sm text-gray-900 bg-transparent 
                                            border-0 border-b-2 border-gray-300 appearance-none focus:outline-none focus:ring-0 
                                            focus:border-blue-600 peer" 
                                placeholder=" " 
                                required 
                                readOnly
                            />
                            <label 
                                htmlFor="fullname" 
                                className="absolute top-3 -z-10 text-sm text-gray-500 
                                            duration-300 transform -translate-y-6 scale-75 origin-[0] peer-focus:left-0 peer-focus:font-medium 
                                            peer-focus:text-blue-600 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 
                                            peer-focus:scale-75 peer-focus:-translate-y-6"
                            >Nama Lengkap</label>
                        </div>
                        {/* button */}
                        <div className="mb-6 w-full relative z-0 flex justify-center gap-5">
                            <button 
                                type="submit" 
                                disabled={!isReady}
                                className={`mt-5 px-5 py-2.5 w-fit rounded-lg sm:w-autorounded-lg font-medium text-sm text-center ${isReady ? 'text-white bg-green-400 hover:bg-green-500 cursor-pointer' : 'text-gray-800 bg-gray-300 cursor-wait'}`}
                            >Submit</button>
                            <div 
                                onClick={handleReset}
                                className={`mt-5 px-5 py-2.5 w-fit rounded-lg sm:w-autorounded-lg font-medium text-sm text-center text-white bg-red-400 hover:bg-red-500 cursor-pointer`}
                            >Reset</div>
                        </div>
                    </form>                    
                </div>
            </div>
        </div>
    )
}

// CardEmployee.propTypes = {
//     education: PropTypes.object,
//     fetchAgain: PropTypes.bool,
//     setFetchAgain: PropTypes.func,
// }

export default CardEmployee